
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Send, X, Image as ImageIcon, Film, Images } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ApprovalPost } from "@/hooks/use-approvals";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const formSchema = z.object({
  title: z.string().min(3, "Título deve ter pelo menos 3 caracteres"),
  caption: z.string().optional(),
  post_date: z.string().optional(),
  post_type: z.enum(["static", "carousel", "reels"])
});

type FormValues = z.infer<typeof formSchema>;

interface ResubmitApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approval: ApprovalPost | null;
  onResubmit: (id: string, data: {
    title?: string;
    caption?: string;
    post_type?: string;
    post_date?: string;
    static_image?: File;
    carousel_images?: File[];
    reels_video?: File;
  }) => Promise<boolean>;
}

export function ResubmitApprovalDialog({
  open,
  onOpenChange,
  approval,
  onResubmit,
}: ResubmitApprovalDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staticImage, setStaticImage] = useState<File | null>(null);
  const [carouselImages, setCarouselImages] = useState<File[]>([]);
  const [reelsVideo, setReelsVideo] = useState<File | null>(null);
  const [staticImagePreview, setStaticImagePreview] = useState<string | null>(null);
  const [carouselPreviews, setCarouselPreviews] = useState<string[]>([]);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: approval?.title || "",
      caption: approval?.caption || "",
      post_date: approval?.post_date ? new Date(approval.post_date).toISOString().split('T')[0] : "",
      post_type: (approval?.post_type as "static" | "carousel" | "reels") || "static",
    },
  });
  
  // Reset form when approval changes
  useEffect(() => {
    if (approval) {
      form.reset({
        title: approval.title,
        caption: approval.caption || "",
        post_date: approval.post_date ? new Date(approval.post_date).toISOString().split('T')[0] : "",
        post_type: (approval.post_type as "static" | "carousel" | "reels") || "static",
      });
      
      // Set previews based on approval type
      if (approval.post_type === "static" && approval.static_image) {
        setStaticImagePreview(approval.static_image);
      } else if (approval.post_type === "carousel" && approval.carousel_images) {
        setCarouselPreviews(approval.carousel_images);
      } else if (approval.post_type === "reels" && approval.reels_video) {
        setVideoPreview(approval.reels_video);
      } else if (approval.image_url) {
        // Backward compatibility
        setStaticImagePreview(approval.image_url);
      }
    }
  }, [approval, form]);
  
  const selectedPostType = form.watch("post_type");
  
  const handleStaticImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setStaticImage(file);
      const objectUrl = URL.createObjectURL(file);
      setStaticImagePreview(objectUrl);
    }
  };
  
  const handleCarouselImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      newFiles.push(files[i]);
      newPreviews.push(URL.createObjectURL(files[i]));
    }
    
    setCarouselImages([...carouselImages, ...newFiles]);
    setCarouselPreviews([...carouselPreviews, ...newPreviews]);
  };
  
  const handleReelsVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReelsVideo(file);
      const objectUrl = URL.createObjectURL(file);
      setVideoPreview(objectUrl);
    }
  };
  
  const removeStaticImage = () => {
    setStaticImage(null);
    if (staticImagePreview) {
      URL.revokeObjectURL(staticImagePreview);
      setStaticImagePreview(null);
    }
  };
  
  const removeCarouselImage = (index: number) => {
    // Remove from files only if it's a newly added file
    if (index < carouselImages.length) {
      const newCarouselImages = [...carouselImages];
      newCarouselImages.splice(index, 1);
      setCarouselImages(newCarouselImages);
    }
    
    // Remove from previews
    const newPreviews = [...carouselPreviews];
    if (index < newPreviews.length) {
      URL.revokeObjectURL(newPreviews[index]);
      newPreviews.splice(index, 1);
      setCarouselPreviews(newPreviews);
    }
  };
  
  const removeReelsVideo = () => {
    setReelsVideo(null);
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    }
  };
  
  const handleSubmit = async (values: FormValues) => {
    if (!approval?.id) return;
    
    setIsSubmitting(true);
    
    try {
      await onResubmit(approval.id, {
        title: values.title,
        caption: values.caption,
        post_date: values.post_date,
        post_type: values.post_type,
        static_image: values.post_type === "static" ? staticImage : undefined,
        carousel_images: values.post_type === "carousel" ? carouselImages : undefined,
        reels_video: values.post_type === "reels" ? reelsVideo : undefined
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error("Error resubmitting approval:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const cleanUp = () => {
    if (staticImagePreview && staticImage) URL.revokeObjectURL(staticImagePreview);
    if (videoPreview && reelsVideo) URL.revokeObjectURL(videoPreview);
    carouselPreviews.forEach(url => {
      if (!url.startsWith('http')) URL.revokeObjectURL(url);
    });
    
    setStaticImage(null);
    setCarouselImages([]);
    setReelsVideo(null);
    setStaticImagePreview(null);
    setCarouselPreviews([]);
    setVideoPreview(null);
  };
  
  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!newOpen) cleanUp();
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar aprovação novamente</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input placeholder="Título do post" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="post_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Post</FormLabel>
                  <FormControl>
                    <Tabs 
                      onValueChange={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value}
                      className="w-full"
                    >
                      <TabsList className="grid grid-cols-3 mb-4">
                        <TabsTrigger value="static" className="flex items-center gap-2">
                          <ImageIcon className="h-4 w-4" />
                          Estático
                        </TabsTrigger>
                        <TabsTrigger value="carousel" className="flex items-center gap-2">
                          <Images className="h-4 w-4" />
                          Carrossel
                        </TabsTrigger>
                        <TabsTrigger value="reels" className="flex items-center gap-2">
                          <Film className="h-4 w-4" />
                          Reels
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="static" className="border rounded-md p-4">
                        <div className="space-y-2">
                          {!staticImagePreview ? (
                            <div className="border-dashed border-2 rounded-md p-10 flex flex-col items-center justify-center">
                              <label htmlFor="static-image-upload" className="cursor-pointer text-center">
                                <ImageIcon className="h-10 w-10 mb-2 mx-auto text-gray-400" />
                                <p className="text-sm font-medium">Clique para fazer upload de uma imagem</p>
                                <p className="text-xs text-gray-500 mt-1">PNG, JPG ou JPEG</p>
                                <input 
                                  id="static-image-upload" 
                                  type="file" 
                                  accept="image/*" 
                                  className="hidden" 
                                  onChange={handleStaticImageChange} 
                                />
                              </label>
                            </div>
                          ) : (
                            <div className="relative">
                              <AspectRatio ratio={1}>
                                <img 
                                  src={staticImagePreview} 
                                  alt="Preview" 
                                  className="rounded-md w-full h-full object-cover"
                                />
                              </AspectRatio>
                              <button
                                type="button"
                                onClick={removeStaticImage}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="carousel" className="border rounded-md p-4">
                        <div className="space-y-4">
                          <div className="border-dashed border-2 rounded-md p-6 flex flex-col items-center justify-center">
                            <label htmlFor="carousel-images-upload" className="cursor-pointer text-center">
                              <Images className="h-8 w-8 mb-2 mx-auto text-gray-400" />
                              <p className="text-sm font-medium">Clique para adicionar imagens</p>
                              <p className="text-xs text-gray-500 mt-1">Até 10 imagens</p>
                              <input 
                                id="carousel-images-upload" 
                                type="file" 
                                accept="image/*" 
                                multiple 
                                className="hidden" 
                                onChange={handleCarouselImagesChange} 
                                disabled={carouselPreviews.length >= 10}
                              />
                            </label>
                          </div>
                          
                          {carouselPreviews.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {carouselPreviews.map((url, index) => (
                                <div key={index} className="relative">
                                  <AspectRatio ratio={1}>
                                    <img 
                                      src={url} 
                                      alt={`Preview ${index + 1}`} 
                                      className="rounded-md w-full h-full object-cover" 
                                    />
                                  </AspectRatio>
                                  <div className="absolute top-2 right-2 flex gap-1">
                                    <button
                                      type="button"
                                      onClick={() => removeCarouselImage(index)}
                                      className="bg-red-500 text-white p-1 rounded-full"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full">
                                    {index + 1}/{carouselPreviews.length}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="reels" className="border rounded-md p-4">
                        <div className="space-y-2">
                          {!videoPreview ? (
                            <div className="border-dashed border-2 rounded-md p-10 flex flex-col items-center justify-center">
                              <label htmlFor="reels-video-upload" className="cursor-pointer text-center">
                                <Film className="h-10 w-10 mb-2 mx-auto text-gray-400" />
                                <p className="text-sm font-medium">Clique para fazer upload de um vídeo</p>
                                <p className="text-xs text-gray-500 mt-1">MP4, MOV ou AVI</p>
                                <input 
                                  id="reels-video-upload" 
                                  type="file" 
                                  accept="video/*" 
                                  className="hidden" 
                                  onChange={handleReelsVideoChange} 
                                />
                              </label>
                            </div>
                          ) : (
                            <div className="relative">
                              <AspectRatio ratio={9/16}>
                                <video 
                                  src={videoPreview} 
                                  controls 
                                  className="rounded-md w-full h-full object-cover"
                                />
                              </AspectRatio>
                              <button
                                type="button"
                                onClick={removeReelsVideo}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="caption"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Legenda</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Legenda do post"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="post_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data de Publicação</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter className="pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                type="button"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" /> Enviar para aprovação
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
